import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Card,
  Table,
  Button,
  Row,
  Col,
  Badge,
  Form,
  OverlayTrigger,
  Tooltip,
  Spinner,
} from "react-bootstrap";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  getFirestore,
} from "firebase/firestore";
import firebaseApp from "../../firebase/credenciales";
import { initializeCollections } from "../../utils/initializeCollections";
import EmpleadoForm from "./EmpleadoForm";
import Swal from "sweetalert2";
import {
  FaUserPlus,
  FaUserEdit,
  FaUserMinus,
  FaSync,
  FaUsers,
  FaSearch,
  FaIdCard,
  FaMoneyBillWave,
  FaUserTie,
  FaBuilding,
  FaChartLine,
} from "react-icons/fa";
import { BsCheckCircleFill, BsFillXCircleFill } from "react-icons/bs";

const db = getFirestore(firebaseApp);

function Empleados({ usuario }) {
  const [empleados, setEmpleados] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedEmpleado, setSelectedEmpleado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [mostrarSoloActivos, setMostrarSoloActivos] = useState(true);
  const [datosIniciales, setDatosIniciales] = useState(false);

  console.log(usuario);

  const cargarEmpleados = useCallback(async () => {
    if (!usuario?.ruc) {
      setEmpleados([]);
      setDatosIniciales(true);
      return;
    }

    try {
      setLoading(true);
      const q = query(
        collection(db, "pl_empleados"),
        where("ruc_empresa", "==", usuario.ruc)
      );
      const querySnapshot = await getDocs(q);
      const empleadosData = [];
      querySnapshot.forEach((doc) => {
        empleadosData.push({ id: doc.id, ...doc.data() });
      });
      setEmpleados(empleadosData);
      setDatosIniciales(true);
    } catch (error) {
      console.error("Error al cargar empleados:", error);
      mostrarError("Error al cargar la lista de empleados");
    } finally {
      setLoading(false);
    }
  }, [usuario?.ruc]);

  useEffect(() => {
    cargarEmpleados();
  }, [cargarEmpleados]);

  const handleInitialize = async () => {
    try {
      setLoading(true);
      const result = await initializeCollections(usuario.ruc);
      if (result.success) {
        Swal.fire({
          title: "¡Éxito!",
          text: "Sistema de planillas inicializado correctamente",
          icon: "success",
        });
        await cargarEmpleados();
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      mostrarError("Error al inicializar el sistema de planillas");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (empleado) => {
    setSelectedEmpleado(empleado);
    setShowModal(true);
  };

  const handleCesar = async (empleado) => {
    try {
      const result = await Swal.fire({
        title: "¿Estás seguro?",
        text: `¿Deseas cesar al empleado ${empleado.nombres} ${empleado.apellidos}?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Sí, cesar",
        cancelButtonText: "Cancelar",
      });

      if (result.isConfirmed) {
        setLoading(true);
        const empleadoRef = doc(db, "pl_empleados", empleado.dni);
        await updateDoc(empleadoRef, {
          activo: false,
          fecha_cese: new Date(),
          updated_at: new Date(),
        });

        Swal.fire(
          "¡Cesado!",
          "El empleado ha sido cesado correctamente.",
          "success"
        );

        cargarEmpleados();
      }
    } catch (error) {
      console.error("Error al cesar empleado:", error);
      mostrarError("Error al cesar al empleado");
    } finally {
      setLoading(false);
    }
  };

  const mostrarError = (mensaje) => {
    Swal.fire({
      icon: "error",
      title: "Error",
      text: mensaje,
    });
  };

  const handleRowSelection = (rowId) => {
    if (selectedRows.includes(rowId)) {
      setSelectedRows(selectedRows.filter((id) => id !== rowId));
    } else {
      setSelectedRows([...selectedRows, rowId]);
    }
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedRows([]);
    } else {
      const allRowIds = empleadosFiltrados.map((item) => item.id);
      setSelectedRows(allRowIds);
    }
    setSelectAll(!selectAll);
  };

  const limpiarBusqueda = () => {
    setSearchTerm("");
  };

  const toggleMostrarEmpleados = () => {
    setMostrarSoloActivos(!mostrarSoloActivos);
    setSelectedRows([]);
    setSelectAll(false);
  };

  // Filtros
  const empleadosFiltrados = empleados.filter((empleado) => {
    const cumpleBusqueda =
      empleado.nombres?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      empleado.apellidos?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      empleado.dni?.toString().includes(searchTerm) ||
      empleado.cargo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      empleado.area?.toLowerCase().includes(searchTerm.toLowerCase());

    const cumpleEstado = mostrarSoloActivos ? empleado.activo === true : true;

    return cumpleBusqueda && cumpleEstado;
  });

  // Estadísticas
  const calcularEmpleadosActivos = () => {
    return empleados.filter((empleado) => empleado.activo === true).length;
  };

  const calcularEmpleadosInactivos = () => {
    return empleados.filter((empleado) => empleado.activo === false).length;
  };

  const calcularSueldoPromedio = () => {
    const empleadosActivos = empleados.filter((emp) => emp.activo === true);
    if (empleadosActivos.length === 0) return 0;
    const sumaTotal = empleadosActivos.reduce(
      (sum, emp) => sum + (Number(emp.sueldo_base) || 0),
      0
    );
    return sumaTotal / empleadosActivos.length;
  };

  const calcularEmpleadosConEssaludVida = () => {
    return empleados.filter(
      (empleado) =>
        empleado.activo === true && Number(empleado.essalud_vida_importe) > 0
    ).length;
  };

  if (loading && !datosIniciales) {
    return (
      <Container className="my-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <div
      className="min-vh-100"
      style={{
        background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
        minHeight: "100vh",
        width: "100vw",
        maxWidth: "100%",
        padding: "20px 0",
      }}
    >
      <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
        {/* Indicador de carga para operaciones */}
        {loading && (
          <div
            className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-dark bg-opacity-75"
            style={{ zIndex: 1080 }}
          >
            <div className="text-center">
              <Spinner
                animation="border"
                variant="light"
                style={{ width: "3rem", height: "3rem" }}
              />
              <p className="text-white mt-3 mb-0">Procesando solicitud...</p>
            </div>
          </div>
        )}

        {/* Marco principal */}
        <Card
          className="shadow-lg border-0"
          style={{
            borderRadius: "15px",
            overflow: "hidden",
            minHeight: "90vh",
          }}
        >
          {/* Header Premium con degradado marrón para Gestión de Empleados */}
          <Card.Header
            className="text-white py-4"
            style={{
              background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
              borderBottom: "3px solid #D2691E",
            }}
          >
            <div className="text-center">
              <h2 className="fw-bold mb-1">
                <FaUserTie className="me-3" />
                Gestión de Empleados
              </h2>
              <p className="mb-0 opacity-75">
                Administra los empleados y su información laboral
              </p>
            </div>
          </Card.Header>

          <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            {/* Indicador de carga inicial */}
            {!datosIniciales && empleados.length === 0 && !loading && (
              <div className="text-center py-5">
                <Spinner animation="border" style={{ color: "#8B4513" }} />
                <p className="text-muted mt-3 mb-0">Cargando empleados...</p>
              </div>
            )}

            {/* Contenido principal */}
            {datosIniciales && (
              <>
                {/* Barra superior con acciones */}
                <Card
                  className="shadow-sm border-0 mb-4"
                  style={{ borderRadius: "15px" }}
                >
                  <Card.Body className="p-4">
                    <Row className="align-items-center">
                      <Col md={1}>
                        <div
                          className="rounded-3 p-3 text-center"
                          style={{
                            background:
                              "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                            color: "white",
                          }}
                        >
                          <FaUserTie size={24} />
                        </div>
                      </Col>
                      <Col md={7}>
                        <h5 className="mb-1 fw-semibold">
                          Gestión de Empleados
                        </h5>
                        <p className="text-muted mb-0 small">
                          Administra empleados, cargos, sueldos y beneficios
                          laborales
                        </p>
                      </Col>
                      <Col md={4} className="text-end">
                        <div className="d-flex gap-2 justify-content-end">
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>Agregar nuevo empleado</Tooltip>}
                          >
                            <Button
                              variant="light"
                              className="d-flex align-items-center gap-2"
                              onClick={() => {
                                setSelectedEmpleado(null);
                                setShowModal(true);
                              }}
                              style={{
                                borderRadius: "10px",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                              }}
                            >
                              <FaUserPlus size="16px" />
                              Nuevo Empleado
                            </Button>
                          </OverlayTrigger>
                          {usuario.rol === "superAdmin" && (
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip>
                                  Inicializar sistema de planillas
                                </Tooltip>
                              }
                            >
                              <Button
                                variant="outline-light"
                                className="d-flex align-items-center gap-2"
                                onClick={handleInitialize}
                                disabled={loading}
                                style={{ borderRadius: "10px" }}
                              >
                                <FaSync size="16px" />
                                Inicializar
                              </Button>
                            </OverlayTrigger>
                          )}
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                {/* Barra de búsqueda */}
                <Card
                  className="shadow-sm border-0 mb-4"
                  style={{ borderRadius: "15px" }}
                >
                  <Card.Body className="p-3">
                    <Row className="g-2 align-items-center">
                      <Col md={10}>
                        <div className="position-relative">
                          <FaSearch
                            className="position-absolute"
                            style={{
                              left: "12px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              color: "#6c757d",
                              zIndex: 2,
                            }}
                          />
                          <Form.Control
                            type="text"
                            placeholder="Buscar empleados por DNI, nombres, apellidos, cargo o área..."
                            className="form-control-lg ps-5"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                              borderRadius: "10px",
                              border: "2px solid #e9ecef",
                            }}
                          />
                        </div>
                      </Col>
                      <Col md={2}>
                        <Button
                          variant="outline-secondary"
                          size="lg"
                          className="w-100 d-flex align-items-center justify-content-center gap-2"
                          onClick={limpiarBusqueda}
                          style={{ borderRadius: "10px" }}
                        >
                          <FaSync size="16px" />
                          Limpiar
                        </Button>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                {/* Filtro de empleados activos/todos */}
                <Card
                  className="shadow-sm border-0 mb-4"
                  style={{ borderRadius: "15px" }}
                >
                  <Card.Body className="p-3">
                    <Row className="align-items-center">
                      <Col md={8}>
                        <div className="d-flex align-items-center gap-3">
                          <div className="d-flex align-items-center gap-2">
                            <div
                              className="rounded-2 p-2"
                              style={{
                                background: mostrarSoloActivos
                                  ? "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)"
                                  : "linear-gradient(135deg, #6b7280 0%, #9ca3af 100%)",
                                color: "white",
                              }}
                            >
                              <FaUsers size={16} />
                            </div>
                            <div>
                              <h6 className="mb-0 fw-semibold">
                                {mostrarSoloActivos
                                  ? "Empleados Activos"
                                  : "Todos los Empleados"}
                              </h6>
                              <small className="text-muted">
                                {mostrarSoloActivos
                                  ? "Mostrando solo empleados activos"
                                  : "Mostrando empleados activos e inactivos"}
                              </small>
                            </div>
                          </div>
                          <Badge
                            bg={mostrarSoloActivos ? "warning" : "secondary"}
                            className="px-3 py-2"
                            style={{
                              fontSize: "0.9rem",
                              backgroundColor: mostrarSoloActivos
                                ? "#8B4513"
                                : undefined,
                            }}
                          >
                            {empleadosFiltrados.length} empleados mostrados
                          </Badge>
                        </div>
                      </Col>
                      <Col md={4} className="text-end">
                        <Button
                          variant={
                            mostrarSoloActivos
                              ? "outline-secondary"
                              : "outline-warning"
                          }
                          onClick={toggleMostrarEmpleados}
                          className="d-flex align-items-center gap-2"
                          style={{
                            borderRadius: "10px",
                            padding: "8px 16px",
                            fontWeight: "500",
                            borderColor: mostrarSoloActivos
                              ? undefined
                              : "#8B4513",
                            color: mostrarSoloActivos ? undefined : "#8B4513",
                          }}
                        >
                          <FaUsers size="14px" />
                          {mostrarSoloActivos ? "Ver Todos" : "Solo Activos"}
                        </Button>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                {/* Estadísticas */}
                <Row className="mb-4">
                  <Col md={3}>
                    <Card
                      className="border-0 shadow-sm"
                      style={{ borderRadius: "15px" }}
                    >
                      <Card.Body
                        className="text-center"
                        style={{
                          background:
                            "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                          color: "white",
                          borderRadius: "15px",
                        }}
                      >
                        <FaUsers size={32} className="mb-2" />
                        <h4 className="fw-bold">
                          {calcularEmpleadosActivos()}
                        </h4>
                        <p className="mb-0">Empleados Activos</p>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card
                      className="border-0 shadow-sm"
                      style={{ borderRadius: "15px" }}
                    >
                      <Card.Body
                        className="text-center"
                        style={{
                          background:
                            "linear-gradient(135deg, #dc2626 0%, #ef4444 100%)",
                          color: "white",
                          borderRadius: "15px",
                        }}
                      >
                        <FaUserMinus size={32} className="mb-2" />
                        <h4 className="fw-bold">
                          {calcularEmpleadosInactivos()}
                        </h4>
                        <p className="mb-0">Empleados Inactivos</p>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card
                      className="border-0 shadow-sm"
                      style={{ borderRadius: "15px" }}
                    >
                      <Card.Body
                        className="text-center"
                        style={{
                          background:
                            "linear-gradient(135deg, #D2691E 0%, #CD853F 100%)",
                          color: "white",
                          borderRadius: "15px",
                        }}
                      >
                        <FaMoneyBillWave size={32} className="mb-2" />
                        <h4 className="fw-bold">
                          S/ {calcularSueldoPromedio().toFixed(0)}
                        </h4>
                        <p className="mb-0">Sueldo Promedio</p>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card
                      className="border-0 shadow-sm"
                      style={{ borderRadius: "15px" }}
                    >
                      <Card.Body
                        className="text-center"
                        style={{
                          background:
                            "linear-gradient(135deg, #B8860B 0%, #DAA520 100%)",
                          color: "white",
                          borderRadius: "15px",
                        }}
                      >
                        <FaChartLine size={32} className="mb-2" />
                        <h4 className="fw-bold">
                          {calcularEmpleadosConEssaludVida()}
                        </h4>
                        <p className="mb-0">Con EsSalud Vida</p>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                {/* Mensaje cuando no hay empleados */}
                {empleadosFiltrados.length === 0 && (
                  <Card
                    className="border-0 shadow-sm mb-4"
                    style={{ borderRadius: "15px" }}
                  >
                    <Card.Body className="text-center py-5">
                      <div
                        className="bg-light rounded-circle mx-auto mb-3"
                        style={{
                          width: "80px",
                          height: "80px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <FaUserTie size={32} className="text-muted" />
                      </div>
                      <h5 className="text-muted mb-2">
                        {mostrarSoloActivos
                          ? "No hay empleados activos"
                          : "No hay empleados disponibles"}
                      </h5>
                      <p className="text-muted mb-3">
                        {searchTerm
                          ? `No se encontraron empleados ${
                              mostrarSoloActivos ? "activos" : ""
                            } que coincidan con tu búsqueda`
                          : mostrarSoloActivos
                          ? "No hay empleados activos en el sistema"
                          : "Agrega tu primer empleado haciendo clic en 'Nuevo Empleado'"}
                      </p>
                      <div className="d-flex gap-2 justify-content-center">
                        {searchTerm && (
                          <Button
                            variant="outline-secondary"
                            onClick={limpiarBusqueda}
                            className="d-flex align-items-center gap-2"
                            style={{ borderRadius: "10px" }}
                          >
                            <FaSync size="14px" />
                            Limpiar búsqueda
                          </Button>
                        )}
                        {mostrarSoloActivos &&
                          calcularEmpleadosInactivos() > 0 && (
                            <Button
                              variant="outline-info"
                              onClick={toggleMostrarEmpleados}
                              className="d-flex align-items-center gap-2"
                              style={{ borderRadius: "10px" }}
                            >
                              <FaUsers size="14px" />
                              Ver todos los empleados
                            </Button>
                          )}
                      </div>
                    </Card.Body>
                  </Card>
                )}

                {/* Tabla de empleados */}
                {empleadosFiltrados.length > 0 && (
                  <Card
                    className="border-0 shadow-sm mb-4"
                    style={{ borderRadius: "15px" }}
                  >
                    <Card.Header
                      className="text-white py-3"
                      style={{
                        background:
                          "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                        borderRadius: "15px 15px 0 0",
                      }}
                    >
                      <div className="d-flex align-items-center">
                        <FaUserTie className="me-2" />
                        <div className="flex-grow-1">
                          <h5 className="mb-0 fw-semibold">
                            Lista de Empleados{" "}
                            {mostrarSoloActivos ? "Activos" : ""}
                          </h5>
                          <small className="opacity-75">
                            {mostrarSoloActivos
                              ? "Empleados activos en planilla"
                              : "Gestiona todos los empleados del sistema"}
                          </small>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <Badge
                            bg="light"
                            text="dark"
                            style={{ fontSize: "0.9rem" }}
                          >
                            {empleadosFiltrados.length} empleados
                          </Badge>
                          <Badge
                            style={{
                              fontSize: "0.8rem",
                              backgroundColor: mostrarSoloActivos
                                ? "#D2691E"
                                : "#6c757d",
                              color: "white",
                            }}
                          >
                            {mostrarSoloActivos ? "Solo Activos" : "Todos"}
                          </Badge>
                        </div>
                      </div>
                    </Card.Header>

                    <Card.Body className="p-0">
                      <div style={{ maxHeight: "600px", overflowY: "auto" }}>
                        <Table
                          hover
                          responsive
                          className="mb-0"
                          style={{ fontSize: "0.9rem" }}
                        >
                          <thead
                            style={{
                              backgroundColor: "#f8f9fa",
                              position: "sticky",
                              top: 0,
                              zIndex: 10,
                            }}
                          >
                            <tr>
                              <th
                                className="border-0 py-3 px-3 text-secondary fw-semibold text-center"
                                style={{ width: "4%" }}
                              >
                                <div className="form-check mb-0">
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={selectAll}
                                    onChange={handleSelectAll}
                                  />
                                </div>
                              </th>
                              <th
                                className="border-0 py-3 px-3 text-secondary fw-semibold text-center"
                                style={{ width: "4%" }}
                              >
                                #
                              </th>
                              <th
                                className="border-0 py-3 px-3 text-secondary fw-semibold text-center"
                                style={{ width: "6%" }}
                              >
                                Estado
                              </th>
                              <th
                                className="border-0 py-3 px-3 text-secondary fw-semibold"
                                style={{ width: "10%" }}
                              >
                                <FaIdCard className="me-1" />
                                DNI
                              </th>
                              <th
                                className="border-0 py-3 px-3 text-secondary fw-semibold"
                                style={{ width: "15%" }}
                              >
                                <FaUserTie className="me-1" />
                                Nombres
                              </th>
                              <th
                                className="border-0 py-3 px-3 text-secondary fw-semibold"
                                style={{ width: "15%" }}
                              >
                                Apellidos
                              </th>
                              <th
                                className="border-0 py-3 px-3 text-secondary fw-semibold"
                                style={{ width: "12%" }}
                              >
                                <FaBuilding className="me-1" />
                                Cargo
                              </th>
                              <th
                                className="border-0 py-3 px-3 text-secondary fw-semibold text-end"
                                style={{ width: "10%" }}
                              >
                                <FaMoneyBillWave className="me-1" />
                                Sueldo Base
                              </th>
                              <th
                                className="border-0 py-3 px-3 text-secondary fw-semibold text-end"
                                style={{ width: "10%" }}
                              >
                                Asignación Familiar
                              </th>
                              <th
                                className="border-0 py-3 px-3 text-secondary fw-semibold text-center"
                                style={{ width: "8%" }}
                              >
                                Régimen
                              </th>
                              <th
                                className="border-0 py-3 px-3 text-secondary fw-semibold text-center"
                                style={{ width: "8%" }}
                              >
                                EsSalud Vida
                              </th>
                              <th
                                className="border-0 py-3 px-3 text-secondary fw-semibold text-center"
                                style={{ width: "15%" }}
                              >
                                Acciones
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {empleadosFiltrados.map((empleado, index) => (
                              <tr
                                key={empleado.id}
                                className={
                                  !empleado.activo ? "table-danger" : ""
                                }
                                style={{ borderBottom: "1px solid #f1f3f4" }}
                              >
                                <td className="text-center py-3 px-3">
                                  <div className="form-check mb-0">
                                    <input
                                      type="checkbox"
                                      className="form-check-input"
                                      checked={selectedRows.includes(
                                        empleado.id
                                      )}
                                      onChange={() =>
                                        handleRowSelection(empleado.id)
                                      }
                                    />
                                  </div>
                                </td>
                                <td className="text-center py-3 px-3">
                                  <Badge
                                    style={{
                                      backgroundColor: "#8B4513",
                                      color: "white",
                                      fontSize: "0.75rem",
                                    }}
                                    className="px-2 py-1"
                                  >
                                    {index + 1}
                                  </Badge>
                                </td>
                                <td className="text-center py-3 px-3">
                                  <OverlayTrigger
                                    placement="top"
                                    overlay={
                                      <Tooltip>
                                        {empleado.activo === true
                                          ? "Empleado Activo"
                                          : empleado.activo === false
                                          ? "Empleado Cesado"
                                          : "Estado no definido"}
                                      </Tooltip>
                                    }
                                  >
                                    {empleado.activo === true ? (
                                      <BsCheckCircleFill
                                        size="18px"
                                        className="text-success"
                                        style={{ cursor: "pointer" }}
                                      />
                                    ) : empleado.activo === false ? (
                                      <BsFillXCircleFill
                                        size="18px"
                                        className="text-danger"
                                        style={{ cursor: "pointer" }}
                                      />
                                    ) : (
                                      <Badge bg="warning" text="dark">
                                        ?
                                      </Badge>
                                    )}
                                  </OverlayTrigger>
                                </td>
                                <td className="py-3 px-3">
                                  <Badge
                                    bg="secondary"
                                    className="px-2 py-1"
                                    style={{ fontSize: "0.75rem" }}
                                  >
                                    {empleado.dni}
                                  </Badge>
                                </td>
                                <td className="py-3 px-3">
                                  <div className="fw-medium text-dark">
                                    {empleado.nombres}
                                  </div>
                                </td>
                                <td className="py-3 px-3 fw-medium text-dark">
                                  {empleado.apellidos}
                                </td>
                                <td className="py-3 px-3">
                                  <Badge
                                    bg="info"
                                    className="px-2 py-1"
                                    style={{ fontSize: "0.75rem" }}
                                  >
                                    {empleado.cargo}
                                  </Badge>
                                </td>
                                <td className="text-end py-3 px-3">
                                  <span className="fw-bold text-success">
                                    S/ {Number(empleado.sueldo_base).toFixed(2)}
                                  </span>
                                </td>
                                <td className="text-center py-3 px-3">
                                  <Badge
                                    bg={
                                      empleado.asignacion_familiar
                                        ? "success"
                                        : "secondary"
                                    }
                                    style={{ fontSize: "0.7rem" }}
                                  >
                                    {empleado.asignacion_familiar ? "Sí" : "No"}
                                  </Badge>
                                </td>
                                <td className="text-center py-3 px-3">
                                  <Badge
                                    bg="primary"
                                    className="px-1 py-1"
                                    style={{ fontSize: "0.7rem" }}
                                  >
                                    {empleado.regimen_pensionario?.substring(
                                      0,
                                      3
                                    )}
                                  </Badge>
                                </td>
                                <td className="text-center py-3 px-3">
                                  <Badge
                                    bg={
                                      Number(empleado.essalud_vida_importe) > 0
                                        ? "success"
                                        : "secondary"
                                    }
                                    style={{ fontSize: "0.7rem" }}
                                  >
                                    {Number(empleado.essalud_vida_importe) > 0
                                      ? "Sí"
                                      : "No"}
                                  </Badge>
                                </td>
                                <td className="text-center py-3 px-3">
                                  <div className="d-flex gap-1 justify-content-center">
                                    <OverlayTrigger
                                      placement="top"
                                      overlay={
                                        <Tooltip>Editar empleado</Tooltip>
                                      }
                                    >
                                      <Button
                                        variant="outline-warning"
                                        size="sm"
                                        onClick={() => handleEdit(empleado)}
                                        disabled={!empleado.activo}
                                        style={{
                                          borderRadius: "6px",
                                          padding: "4px 8px",
                                        }}
                                      >
                                        <FaUserEdit size="12px" />
                                      </Button>
                                    </OverlayTrigger>
                                    {empleado.activo && (
                                      <OverlayTrigger
                                        placement="top"
                                        overlay={
                                          <Tooltip>Cesar empleado</Tooltip>
                                        }
                                      >
                                        <Button
                                          variant="outline-danger"
                                          size="sm"
                                          onClick={() => handleCesar(empleado)}
                                          style={{
                                            borderRadius: "6px",
                                            padding: "4px 8px",
                                          }}
                                        >
                                          <FaUserMinus size="12px" />
                                        </Button>
                                      </OverlayTrigger>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </Card.Body>
                  </Card>
                )}

                {/* Barra de acciones */}
                {datosIniciales && (
                  <Card
                    className="border-0 shadow-sm"
                    style={{ borderRadius: "15px" }}
                  >
                    <Card.Body className="p-4">
                      <Row className="g-3">
                        <Col md={6}>
                          <Button
                            variant="primary"
                            onClick={() => {
                              setSelectedEmpleado(null);
                              setShowModal(true);
                            }}
                            className="w-100 d-flex align-items-center justify-content-center gap-2 py-3"
                            style={{
                              borderRadius: "10px",
                              background:
                                "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                              border: "none",
                              boxShadow: "0 4px 8px rgba(139, 69, 19, 0.3)",
                            }}
                          >
                            <FaUserPlus size="16px" />
                            Añadir Empleado
                          </Button>
                        </Col>

                        <Col md={6}>
                          <Button
                            variant="info"
                            onClick={() => window.location.reload()}
                            className="w-100 d-flex align-items-center justify-content-center gap-2 py-3 text-white"
                            style={{
                              borderRadius: "10px",
                              background:
                                "linear-gradient(135deg, #D2691E 0%, #CD853F 100%)",
                              border: "none",
                              boxShadow: "0 4px 8px rgba(210, 105, 30, 0.3)",
                            }}
                          >
                            <FaSync size="16px" />
                            Actualizar Lista
                          </Button>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                )}
              </>
            )}
          </Card.Body>
        </Card>

        {showModal && (
          <EmpleadoForm
            show={showModal}
            onHide={() => {
              setShowModal(false);
              setSelectedEmpleado(null);
            }}
            empleado={selectedEmpleado}
            usuario={usuario}
            onSave={cargarEmpleados}
          />
        )}
      </Container>
    </div>
  );
}

export default Empleados;
